import { inject, Injectable } from '@angular/core';
import { ConnectorApiService } from './connector-api.service';
import {
  StructuredLogger,
  logCorrelationId,
  logFailureReason,
} from '@brighthub/logging';

const MCP_SERVER_KEY = 'oauth_connector_mcp_server_id';
const RETURN_TO_KEY = 'oauth_return_to';
const FALLBACK_RETURN_PATH = '/ask-navi';

export const CONNECTOR_POPUP_TIMEOUT_MS = 5 * 60 * 1000;
export const CONNECTOR_POPUP_CLOSE_POLL_MS = 500;

class ConnectorConnectionError extends Error {
  constructor(
    readonly reason:
      | 'popup_blocked'
      | 'timeout'
      | 'contract_mismatch'
      | 'authorization_failed'
      | 'cancelled',
    message: string,
  ) {
    super(message);
  }
}

@Injectable({ providedIn: 'root' })
export class ConnectorOAuthService {
  private readonly api = inject(ConnectorApiService);
  private readonly logs = inject(StructuredLogger);
  private readonly inFlight = new Map<string, Promise<void>>();
  private readonly failedConnections = new Map<
    string,
    { interactionId: string; attempts: number; updated: number }
  >();

  connectInPopup(mcpServerId: string, returnTo = currentReturnPath()): Promise<void> {
    const active = this.inFlight.get(mcpServerId);
    if (active) return active;

    const prior = this.failedConnections.get(mcpServerId);
    const previous =
      prior && Date.now() - prior.updated < 10 * 60_000 ? prior : undefined;
    const interactionId = previous?.interactionId ?? logCorrelationId();
    const started = Date.now();
    const context = {
      feature: 'connectors',
      stage: 'connection',
      interaction_id: interactionId,
      attempt_count: (previous?.attempts ?? 0) + 1,
    };
    const connection = this.startPopupConnection(mcpServerId, returnTo)
      .then(
        () => {
          if (previous)
            this.logs.emit('connector.connection.recovered', {
              ...context,
              outcome: 'recovered',
              duration_ms: Math.max(0, Date.now() - started),
            });
          this.failedConnections.delete(mcpServerId);
        },
        (error: unknown) => {
          if (
            error instanceof ConnectorConnectionError &&
            error.reason === 'cancelled'
          ) {
            this.failedConnections.delete(mcpServerId);
            throw error;
          }
          const failure = logFailureReason(error);
          this.logs.emit('connector.connection.failed', {
            ...context,
            ...failure,
            ...(error instanceof ConnectorConnectionError
              ? { reason_code: error.reason }
              : {}),
            outcome: 'failed',
            duration_ms: Math.max(0, Date.now() - started),
          });
          this.failedConnections.set(mcpServerId, {
            interactionId,
            attempts: context.attempt_count,
            updated: Date.now(),
          });
          const oldest = this.failedConnections.keys().next().value;
          if (this.failedConnections.size > 128 && oldest !== undefined)
            this.failedConnections.delete(oldest);
          throw error;
        },
      )
      .finally(() => {
        if (this.inFlight.get(mcpServerId) === connection) {
          this.inFlight.delete(mcpServerId);
        }
      }
    );
    this.inFlight.set(mcpServerId, connection);
    return connection;
  }

  storeReturnContext(mcpServerId: string, returnTo = currentReturnPath()): void {
    sessionStorage.setItem(MCP_SERVER_KEY, mcpServerId);
    sessionStorage.setItem(RETURN_TO_KEY, safeReturnPath(returnTo));
  }

  readReturnContext(): { mcpServerId: string; returnTo: string } {
    return {
      mcpServerId: sessionStorage.getItem(MCP_SERVER_KEY)?.trim() ?? '',
      returnTo: safeReturnPath(sessionStorage.getItem(RETURN_TO_KEY)),
    };
  }

  clearReturnContext(): void {
    sessionStorage.removeItem(MCP_SERVER_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  }

  notifyOpenerConnected(mcpServerId: string): boolean {
    if (!window.opener || window.opener === window) return false;
    window.opener.postMessage(
      { type: 'connector-connected', mcpServerId },
      window.location.origin
    );
    return true;
  }

  /**
   * Reports a callback failure to the same-origin window that opened the OAuth popup.
   *
   * @param mcpServerId - The connector identity, or an empty string when callback context was lost.
   * @param error - A user-safe reason for the failed connection.
   * @returns Whether an opener was available to receive the failure.
   */
  notifyOpenerFailed(mcpServerId: string, error: string): boolean {
    if (!window.opener || window.opener === window) return false;
    window.opener.postMessage(
      { type: 'connector-failed', mcpServerId, error },
      window.location.origin
    );
    return true;
  }

  private async startPopupConnection(
    mcpServerId: string,
    returnTo: string
  ): Promise<void> {
    this.storeReturnContext(mcpServerId, returnTo);
    const popupName = `connect-connector-${mcpServerId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    const popup = window.open('', popupName, 'width=600,height=760');
    if (!popup) {
      throw new ConnectorConnectionError(
        'popup_blocked',
        'Popup blocked. Allow popups for this site and select Connect.',
      );
    }

    let initiated: Awaited<ReturnType<ConnectorApiService['initiateConnector']>>;
    try {
      initiated = await this.api.initiateConnector(mcpServerId);
    } catch (error) {
      closePopup(popup);
      throw error;
    }
    if (initiated.already_connected) {
      closePopup(popup);
      return;
    }
    if (!initiated.authorization_url) {
      closePopup(popup);
      throw new ConnectorConnectionError(
        'contract_mismatch',
        'No authorization URL returned.',
      );
    }
    let authorizationUrl: string;
    try {
      authorizationUrl = secureAuthorizationUrl(initiated.authorization_url);
    } catch (error) {
      closePopup(popup);
      throw error;
    }
    popup.location.href = authorizationUrl;

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        if (settled) return false;
        settled = true;
        window.removeEventListener('message', onMessage);
        window.clearInterval(closePoll);
        window.clearTimeout(timeout);
        closePopup(popup);
        return true;
      };
      const onMessage = (event: MessageEvent) => {
        if (
          event.origin !== window.location.origin ||
          event.source !== popup ||
          !isRecord(event.data)
        ) {
          return;
        }
        const type = event.data['type'];
        const resultMcpServerId = stringValue(event.data['mcpServerId']);
        if (
          type === 'connector-connected' &&
          resultMcpServerId === mcpServerId &&
          cleanup()
        ) {
          resolve();
          return;
        }
        if (
          type === 'connector-failed' &&
          (!resultMcpServerId || resultMcpServerId === mcpServerId) &&
          cleanup()
        ) {
          reject(
            new ConnectorConnectionError(
              'authorization_failed',
              stringValue(event.data['error']) ??
                'The connector could not be connected.'
            )
          );
        }
      };
      const closePoll = window.setInterval(() => {
        if (popup.closed && cleanup()) {
          reject(
            new ConnectorConnectionError('cancelled', 'Connection cancelled.'),
          );
        }
      }, CONNECTOR_POPUP_CLOSE_POLL_MS);
      const timeout = window.setTimeout(() => {
        if (cleanup())
          reject(
            new ConnectorConnectionError('timeout', 'Connection timed out.'),
          );
      }, CONNECTOR_POPUP_TIMEOUT_MS);
      window.addEventListener('message', onMessage);
    });
  }
}

function currentReturnPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function safeReturnPath(value: string | null | undefined): string {
  if (!value?.startsWith('/') || value.startsWith('//')) {
    return FALLBACK_RETURN_PATH;
  }
  try {
    const resolved = new URL(value, window.location.origin);
    return resolved.origin === window.location.origin
      ? `${resolved.pathname}${resolved.search}${resolved.hash}`
      : FALLBACK_RETURN_PATH;
  } catch {
    return FALLBACK_RETURN_PATH;
  }
}

function secureAuthorizationUrl(value: string): string {
  try {
    const url = new URL(value);
    if (url.protocol === 'https:') return url.href;
  } catch {
    // Fall through to the connector-specific error below.
  }
  throw new ConnectorConnectionError(
    'contract_mismatch',
    'The connector did not return a secure authorization URL.',
  );
}

function closePopup(popup: Window): void {
  try {
    popup.close();
  } catch {
    // The browser may revoke access after cross-origin navigation.
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
